import { useEffect, useMemo, useState } from 'react'
import { useAdminAuth } from '@/contexts/AdminAuthContext'
import { useToast } from '@shared/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Label } from '@shared/components/ui/label'
import { Input } from '@shared/components/ui/input'
import { Button } from '@shared/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/avatar'
import { Upload, Trash2, User as UserIcon, Loader2 } from 'lucide-react'
import { apiUrl } from '@/lib/api'

type AdminProfileUI = {
	displayName: string
	email: string
}

const DEFAULT_PROFILE: AdminProfileUI = {
	displayName: '',
	email: '',
}

export default function AdminAccountSettingsPage() {
	const { currentUser, adminUser, token, operationLoading } = useAdminAuth()
	const { toast } = useToast()

	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [profile, setProfile] = useState<AdminProfileUI>(DEFAULT_PROFILE)
	const [emailForm, setEmailForm] = useState<{ currentPassword: string; newEmail: string }>({ currentPassword: '', newEmail: '' })
	const [passwordForm, setPasswordForm] = useState<{ currentPassword: string; newPassword: string; confirmPassword: string }>({ currentPassword: '', newPassword: '', confirmPassword: '' })
	const [emailUpdating, setEmailUpdating] = useState(false)
	const [passwordUpdating, setPasswordUpdating] = useState(false)

	const lastSignIn = useMemo(() => {
		const t = currentUser?.metadata?.lastSignInTime
		return t ? new Date(t).toLocaleString() : ''
	}, [currentUser?.metadata?.lastSignInTime])

	useEffect(() => {
		// Initialize from current profile
		const email = currentUser?.email || adminUser?.email || ''
		const displayName = adminUser?.display_name || email
		setProfile(prev => ({ ...prev, email, displayName }))
		setLoading(false)
	}, [currentUser?.email, adminUser?.display_name, adminUser?.email])

	const handleSave = async () => {
		try {
			setSaving(true)
			const response = await fetch(apiUrl('/api/admin/auth/profile'), {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
				body: JSON.stringify({
					display_name: profile.displayName,
				})
			})

			if (!response.ok) {
				const error = await response.json()
				throw new Error(error.error || 'Failed to update profile')
			}

			toast({ title: 'Saved', description: 'Profile updated successfully' })
		} catch (e: any) {
			toast({ title: 'Error', description: e?.message || 'Failed to save profile', variant: 'destructive' })
		} finally {
			setSaving(false)
		}
	}

	const handleChangeEmail = async () => {
		if (!emailForm.newEmail || !emailForm.currentPassword) {
			toast({ title: 'Missing fields', description: 'Provide new email and current password', variant: 'destructive' })
			return
		}

		setEmailUpdating(true)
		try {
			const response = await fetch(apiUrl('/api/admin/auth/change-email'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
				body: JSON.stringify({
					newEmail: emailForm.newEmail,
					currentPassword: emailForm.currentPassword
				})
			})

			if (!response.ok) {
				const error = await response.json()
				throw new Error(error.error || 'Failed to change email')
			}

			setEmailForm({ currentPassword: '', newEmail: '' })
			toast({ title: 'Verification email sent', description: 'Check your new email inbox and confirm to complete the update' })
		} catch (err: any) {
			toast({ title: 'Failed to start email update', description: err?.message || 'Unable to update email', variant: 'destructive' })
		} finally {
			setEmailUpdating(false)
		}
	}

	const handleChangePassword = async () => {
		if (!passwordForm.currentPassword || !passwordForm.newPassword) {
			toast({ title: 'Missing fields', description: 'Provide current and new password', variant: 'destructive' })
			return
		}
		if (passwordForm.newPassword !== passwordForm.confirmPassword) {
			toast({ title: 'Passwords do not match', description: 'Confirm password must match new password', variant: 'destructive' })
			return
		}

		setPasswordUpdating(true)
		try {
			const response = await fetch(apiUrl('/api/admin/auth/change-password'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
				body: JSON.stringify({
					currentPassword: passwordForm.currentPassword,
					newPassword: passwordForm.newPassword
				})
			})

			if (!response.ok) {
				const error = await response.json()
				throw new Error(error.error || 'Failed to change password')
			}

			setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
			toast({ title: 'Password updated' })
		} catch (err: any) {
			toast({ title: 'Failed to update password', description: err?.message || 'Unable to update password', variant: 'destructive' })
		} finally {
			setPasswordUpdating(false)
		}
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[40vh]">
				<span className="text-muted-foreground">Loading...</span>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Account Settings</CardTitle>
					<CardDescription>Manage your admin profile information</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="flex items-center gap-4">
						<Avatar className="h-20 w-20">
							<AvatarFallback>
								<UserIcon className="h-10 w-10" />
							</AvatarFallback>
						</Avatar>
						<div className="space-y-2">
							<p className="text-sm text-muted-foreground">Admin profile picture not available</p>
						</div>
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<div>
							<Label htmlFor="displayName">Full Name</Label>
							<Input 
								id="displayName" 
								value={profile.displayName} 
								onChange={(e) => setProfile({ ...profile, displayName: e.target.value })} 
							/>
						</div>
						<div>
							<Label htmlFor="email">Email</Label>
							<Input id="email" type="email" value={profile.email} disabled />
						</div>
					</div>

					{lastSignIn && (
						<div className="text-sm text-muted-foreground">Last sign-in: {lastSignIn}</div>
					)}

					<div className="flex justify-end">
						<Button onClick={handleSave} disabled={saving || operationLoading}>
							{saving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>) : 'Save Changes'}
						</Button>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Change Email</CardTitle>
					<CardDescription>Update the email associated with your admin account</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-4 sm:grid-cols-2">
						<div className="sm:col-span-2">
							<Label htmlFor="newEmail">New Email</Label>
							<Input 
								id="newEmail" 
								type="email" 
								value={emailForm.newEmail} 
								onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })} 
								placeholder="name@example.com" 
							/>
						</div>
						<div className="sm:col-span-2">
							<Label htmlFor="currentPasswordForEmail">Current Password</Label>
							<Input 
								id="currentPasswordForEmail" 
								type="password" 
								value={emailForm.currentPassword} 
								onChange={(e) => setEmailForm({ ...emailForm, currentPassword: e.target.value })} 
								placeholder="Enter current password" 
							/>
						</div>
					</div>
					<div className="flex justify-end">
						<Button onClick={handleChangeEmail} disabled={emailUpdating}>
							{emailUpdating ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : 'Send Verification Email'}
						</Button>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Change Password</CardTitle>
					<CardDescription>Reauthenticate to change your password</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-4 sm:grid-cols-2">
						<div>
							<Label htmlFor="currentPassword">Current Password</Label>
							<Input 
								id="currentPassword" 
								type="password" 
								value={passwordForm.currentPassword} 
								onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} 
							/>
						</div>
						<div>
							<Label htmlFor="newPassword">New Password</Label>
							<Input 
								id="newPassword" 
								type="password" 
								value={passwordForm.newPassword} 
								onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} 
							/>
						</div>
						<div className="sm:col-span-2">
							<Label htmlFor="confirmPassword">Confirm New Password</Label>
							<Input 
								id="confirmPassword" 
								type="password" 
								value={passwordForm.confirmPassword} 
								onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} 
							/>
						</div>
					</div>
					<div className="flex justify-end">
						<Button onClick={handleChangePassword} disabled={passwordUpdating}>
							{passwordUpdating ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</>) : 'Update Password'}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
